import { Paper, Title, SimpleGrid, Text, Stack, Center, Loader, ThemeIcon, Group, Badge, Table, Progress, Tooltip } from '@mantine/core';
import { IconTarget, IconBrandGoogle, IconAlertTriangle, IconCheck, IconClock, IconX, IconFileCheck, IconShoppingCart, IconQrcode, IconCalendar, IconPhone } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { ConversionService, ConversionStats } from '@/services/ConversionService';
import { useRefreshInterval } from './RefreshContext';

const GOAL_LABELS: Record<string, string> = {
  DemoRequested: 'Demo angefragt',
  MerchantRegistrationCompleted: 'Reg. abgeschlossen',
  MerchantProfileCompleted: 'Profil fertig',
  OfferCreated: 'Angebot erstellt',
  OfferSubmitted: 'Angebot eingereicht',
  OfferApproved: 'Angebot genehmigt',
  OfferPublished: 'Angebot live',
  FirstQrRedeemed: 'Erste QR-Einl.',
  MerchantActive30Days: '30 Tage aktiv',
};

function useConversions() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'conversions'],
    queryFn: () => ConversionService.getStats(7, 20),
    refetchInterval: interval,
  });
}

function FunnelBar({ funnel }: { funnel: ConversionStats['funnel'] }) {
  const steps = [
    { key: 'demoRequested', label: 'Demo angefragt', value: funnel.demoRequested, icon: IconPhone, color: 'grape' },
    { key: 'registrationCompleted', label: 'Abgeschlossen', value: funnel.registrationCompleted, icon: IconCheck, color: 'cyan' },
    { key: 'profileCompleted', label: 'Profil', value: funnel.profileCompleted, icon: IconFileCheck, color: 'teal' },
    { key: 'offerPublished', label: 'Angebot live', value: funnel.offerPublished, icon: IconShoppingCart, color: 'green' },
    { key: 'firstQrRedeemed', label: 'QR eingelöst', value: funnel.firstQrRedeemed, icon: IconQrcode, color: 'lime' },
    { key: 'active30Days', label: '30d aktiv', value: funnel.active30Days, icon: IconCalendar, color: 'yellow' },
  ];

  const maxValue = Math.max(...steps.map(s => s.value), 1);

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>Conversion Funnel (7 Tage)</Text>
      {steps.map((step, i) => {
        const Icon = step.icon;
        const prevStep = steps[i - 1];
        const rate = prevStep && prevStep.value > 0
          ? Math.round((step.value / prevStep.value) * 100)
          : null;
        return (
          <Group key={step.key} gap="xs" wrap="nowrap">
            <ThemeIcon size="sm" variant="light" color={step.color}>
              <Icon size={14} />
            </ThemeIcon>
            <Text size="xs" w={90} truncate>{step.label}</Text>
            <Progress.Root size="lg" style={{ flex: 1 }}>
              <Tooltip label={`${step.value} (${Math.round((step.value / maxValue) * 100)}%)`}>
                <Progress.Section value={(step.value / maxValue) * 100} color={step.color}>
                  <Progress.Label>{step.value}</Progress.Label>
                </Progress.Section>
              </Tooltip>
            </Progress.Root>
            {rate !== null && (
              <Text size="xs" c="dimmed" w={40} ta="right">{rate}%</Text>
            )}
          </Group>
        );
      })}
    </Stack>
  );
}

function GoogleAdsStatusBadge({ status }: { status?: string }) {
  if (!status) return <Badge size="xs" color="gray" variant="light">-</Badge>;
  switch (status) {
    case 'uploaded':
      return <Badge size="xs" color="green" variant="light" leftSection={<IconCheck size={10} />}>Uploaded</Badge>;
    case 'queued':
      return <Badge size="xs" color="blue" variant="light" leftSection={<IconClock size={10} />}>Queued</Badge>;
    case 'failed':
      return <Badge size="xs" color="red" variant="light" leftSection={<IconX size={10} />}>Failed</Badge>;
    default:
      return <Badge size="xs" color="gray" variant="light">{status}</Badge>;
  }
}

export function ConversionsPanel() {
  const { data, isLoading, error } = useConversions();

  if (error) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="md">Conversions</Title>
        <Text c="red">Fehler beim Laden</Text>
      </Paper>
    );
  }

  if (isLoading || !data) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="md">Conversions</Title>
        <Center><Loader size="sm" /></Center>
      </Paper>
    );
  }

  const hasQueueIssues = data.queueStats.dlqMessages > 0;

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={4}>Conversions (7 Tage)</Title>
        <Group gap="xs">
          {hasQueueIssues && (
            <Badge color="red" variant="filled" size="sm" leftSection={<IconAlertTriangle size={12} />}>
              {data.queueStats.dlqMessages} DLQ
            </Badge>
          )}
          <Badge color="blue" variant="light" size="sm">{data.total} total</Badge>
        </Group>
      </Group>

      <SimpleGrid cols={4} mb="md">
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color="blue">
            <IconTarget size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.byStatus.tracked}</Text>
          <Text size="xs" c="dimmed">Getrackt</Text>
        </Stack>
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color="teal">
            <IconBrandGoogle size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.byStatus.googleAdsUploaded}</Text>
          <Text size="xs" c="dimmed">Google Ads</Text>
        </Stack>
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color="orange">
            <IconClock size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.byStatus.googleAdsQueued}</Text>
          <Text size="xs" c="dimmed">In Queue</Text>
        </Stack>
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color={data.byStatus.googleAdsFailed > 0 ? 'red' : 'gray'}>
            <IconAlertTriangle size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.byStatus.googleAdsFailed}</Text>
          <Text size="xs" c="dimmed">Fehlgeschlagen</Text>
        </Stack>
      </SimpleGrid>

      <FunnelBar funnel={data.funnel} />

      {data.recentConversions.length > 0 && (
        <>
          <Title order={5} mt="md" mb="xs">Letzte Conversions</Title>
          <Table.ScrollContainer minWidth={500}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Zeit</Table.Th>
                  <Table.Th>Goal</Table.Th>
                  <Table.Th>Merchant</Table.Th>
                  <Table.Th>Click ID</Table.Th>
                  <Table.Th>Google Ads</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.recentConversions.slice(0, 10).map((c) => (
                  <Table.Tr key={c.conversionId}>
                    <Table.Td>
                      <Text size="xs">{new Date(c.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light">{GOAL_LABELS[c.goal] || c.goal}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" truncate maw={120}>{c.merchantId}</Text>
                    </Table.Td>
                    <Table.Td>
                      {c.hasClickId ? (
                        <Badge size="xs" color="green" variant="light">gclid</Badge>
                      ) : (
                        <Badge size="xs" color="gray" variant="light">-</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <GoogleAdsStatusBadge status={c.googleAdsStatus} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}
    </Paper>
  );
}
